import { memo, useMemo, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { motion, useAnimation, useInView, type Variants } from 'motion/react'
import { mq, designGrid, vw, getTransition, getP50, getP24 } from '@/styles'
import { SliderFlickity } from './commons'
import { useTranslator } from '@/hooks'
import { CardProduct } from './CardProduct'
import { TypeCmsHomepage } from '@/schema'

interface ISectionFavourites {
  data: TypeCmsHomepage['highlighted_product'],
  isSmallHeadline?: boolean
}


const getFavourites = (products) => products.map((product, idx) => {
  return (
    <DivSlide key={idx} className='slide'>
      <CardProduct key={product.name + idx} data={product} lazyLoad={idx > 4} />
    </DivSlide>


  )
})

export const SectionFavourites = memo<ISectionFavourites>(({ data, isSmallHeadline = false }) => {
  const Favourites = useMemo(() => getFavourites(data), [data])
  const { t } = useTranslator()
  const controls = useAnimation()
  const ref = useRef(null)
  const isInView = useInView(ref, { margin: '0px 0px -100px 0px' })


  useEffect(() => {
    if(isInView) {
      controls.start('animateText')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView])

  return (
    <Section className={isSmallHeadline ? 'isSmallHeadline' : ''}>
      <motion.h2 ref={ref} variants={titleVariants} animate={controls} initial={'initial'}>{t('project.home.favourites')}</motion.h2>
      <div>
        <SliderFlickity className={'slider--product-card'} options={{pageDots: false, wrapAround: false }} >
          {Favourites}
        </SliderFlickity>
      </div>
    </Section>
  )
})



const titleVariants:Variants = {
  initial: {
    y: '20px',
    opacity: 0,
    transition: {
      ...getTransition()
    }
  },
  animateText: {
    y: '0px',
    opacity: 1,
    transition: {
      ...getTransition(),
      ease: [.32,0,.29,.99],
      duration: 1
    }
  },
  exit: {
    y: '20px',
    opacity: 0,
    transition: {
      ...getTransition(),
      duration: .5
    }
  },
}

const Section = styled.section`
	${designGrid({})}
	overflow-x: hidden;
	padding: 90px 0 100px;
	position: relative;

	${mq.greaterThan('tablet')}{
		padding: 110px 0 100px;
	}

	h2 {
		${getP50()}
		grid-area: 1 / 1 / span 1 / span 6;
		margin-bottom: ${vw(60, 'mobile')};
		text-align: left;

		${mq.greaterThan('tablet')}{
			grid-area: 1 / 1 / span 1 / span 6;
			margin-bottom: ${vw(90, 'desktop')};
		}
	}

	&.isSmallHeadline h2 {
		${getP24()}
		margin-bottom: ${vw(40, 'mobile')};

		${mq.greaterThan('tablet')}{
			grid-area: 1 / 1 / span 1 / span 3;
			margin-bottom: ${vw(40, 'desktop')};
		}
	}

	> div {
		display: grid;
		grid-area: 2 / 1 / span 1 / span 6;
		grid-row-gap: ${vw(20, 'mobile')};
		grid-template-columns: 1fr 1fr;
		overflow: hidden;

		${mq.greaterThan('tablet')}{
			grid-area: 2 / 1 / span 1 / span 12;
			grid-template-columns: repeat(4, 1fr);
			grid-row-gap: 0;
		}
	}
`


const DivSlide = styled.div`
  margin-right: ${vw(23, 'mobile')};
  width: auto;

  ${mq.greaterThan('tablet')} {
    margin-right: ${vw(23, 'desktop')};
  }

  &:last-child {
    margin-right: 0;
  }

  div.media {
    min-width: ${vw(271, 'mobile')};

    ${mq.greaterThan('tablet')} {
      min-width: ${vw(320, 'desktop')};
    }
  }

  article {
    height: auto !important;
  }

.media-wrapper {
  height: ${vw(195, 'mobile')} !important;
  width: ${vw(172, 'mobile')};

  ${mq.greaterThan('tablet')} {
    height: ${vw(540, 'desktop')} !important;
    width:${vw(436, 'desktop')};
  }
}

`

